import { Box, Text } from '@pancakeswap/uikit'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import Fold from './Fold'
import { Panel } from './Shell'
import { Display } from '../ui'
import { RulesInput, rulesFileOf, rulesSummary, shortHash } from '../rules'

const List = styled.ol`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 12px;
  li {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }
  .n {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    font-family: 'Martian Mono', ui-monospace, monospace;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.primaryBright};
    border: 1px solid rgba(127, 212, 232, 0.4);
    background: rgba(127, 212, 232, 0.1);
  }
  b {
    display: block;
    font-size: 15px;
  }
  p {
    margin: 2px 0 0;
    font-size: 14px;
    line-height: 21px;
    color: ${({ theme }) => theme.colors.textSubtle};
    overflow-wrap: anywhere;
  }
`

/** "Competition rules": a short plain-English summary of this competition's on-chain rules, with its pool, window, wallet rule, type, split, refund date, risks and rules hash. */
export default function RulesSummary(r: RulesInput) {
  const file = rulesFileOf(r.type)
  // a link to #rules (the trade panel has one) opens the section
  const [signal, setSignal] = useState(0)
  useEffect(() => {
    const check = () => {
      if (window.location.hash === '#rules') setSignal((n) => n + 1)
    }
    check()
    window.addEventListener('hashchange', check)
    return () => window.removeEventListener('hashchange', check)
  }, [])
  return (
    <Fold id="rules" testId="v3-rules" title="Rules" openSignal={signal} hint={`Only this pool, only this window. Top ${r.winners} share the prize. Tap to read.`}>
      <List>
        {rulesSummary(r).map((item, i) => (
          <li key={item.id} data-testid={`v3-rule-${item.id}`}>
            <span className="n" aria-hidden="true">{i + 1}</span>
            <div>
              <b>{item.title}</b>
              <p>{item.text}</p>
            </div>
          </li>
        ))}
      </List>
      <Box mt="16px" style={{ borderTop: '1px solid rgba(180,200,255,0.12)', paddingTop: 12 }}>
        <Text fontSize="13px" style={{ overflowWrap: 'anywhere' }}>
          Rules hash <span style={{ fontFamily: "'Martian Mono', ui-monospace, monospace" }} title={r.rulesHash} data-testid="v3-rules-hash">{shortHash(r.rulesHash)}</span>: the keccak256 of the published rules file, stored with this competition on-chain.{' '}
          {file && <a href={file} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }} data-testid="v3-rules-file">Read the rules file</a>}
        </Text>
        <Text fontSize="12px" color="textSubtle" mt="8px">This is a plain summary of the on-chain rules, not our Terms of Service. If it ever differs from the rules file, the rules file is what the contract uses.</Text>
      </Box>
    </Fold>
  )
}

const More = styled.details`
  margin-top: 14px;
  border-top: 1px solid rgba(180, 200, 255, 0.12);
  summary {
    cursor: pointer;
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 44px;
    font-size: 14px;
    font-weight: 600;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary::after {
    content: '+';
    font-size: 18px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  &[open] summary::after {
    content: '−';
  }
`

const FIRST = ['pool-window', 'score', 'prize']

/**
 * "Rules & scoring" as an open card: the three rules a trader needs before the start (pool and window, how the score works,
 * the prize), the rest behind one fold. The twin of the trade card when the competition is not live (layout.ts).
 */
export function RulesCard(r: RulesInput) {
  const items = rulesSummary(r)
  const file = rulesFileOf(r.type)
  const first = FIRST.map((id) => items.find((i) => i.id === id)).filter(Boolean) as typeof items
  const rest = items.filter((i) => !FIRST.includes(i.id))
  return (
    <Panel id="rules" data-testid="v3-rules" data-variant="card">
      <Display bold fontSize="20px">Rules & scoring</Display>
      <List>
        {first.map((item, i) => (
          <li key={item.id} data-testid={`v3-rule-${item.id}`}>
            <span className="n" aria-hidden="true">{i + 1}</span>
            <div>
              <b>{item.title}</b>
              <p>{item.text}</p>
            </div>
          </li>
        ))}
      </List>
      <More data-testid="v3-rules-more">
        <summary>All {items.length} rules and the rules hash</summary>
        <List>
          {rest.map((item, i) => (
            <li key={item.id} data-testid={`v3-rule-${item.id}`}>
              <span className="n" aria-hidden="true">{first.length + i + 1}</span>
              <div>
                <b>{item.title}</b>
                <p>{item.text}</p>
              </div>
            </li>
          ))}
        </List>
        <Text fontSize="13px" mt="12px" style={{ overflowWrap: 'anywhere' }}>
          Rules hash <span style={{ fontFamily: "'Martian Mono', ui-monospace, monospace" }} title={r.rulesHash} data-testid="v3-rules-hash">{shortHash(r.rulesHash)}</span>: the keccak256 of the published rules file, stored with this competition on-chain.{' '}
          {file && <a href={file} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }} data-testid="v3-rules-file">Read the rules file</a>}
        </Text>
        <Text fontSize="12px" color="textSubtle" mt="8px">This is a plain summary of the on-chain rules, not our Terms of Service. If it ever differs from the rules file, the rules file is what the contract uses.</Text>
      </More>
    </Panel>
  )
}
